import React from 'react';
import { Box, Button, IconButton, InputBase, Typography } from '@material-ui/core';
import { Add, Close, DragIndicator } from '@material-ui/icons';
import { DragDropContext, Draggable, Droppable, DropResult } from '@campfire/draggable-list';
import { v4 as uuidv4 } from 'uuid';
import { ProgramLayout } from '../program-management-actions';
import { ProgramManagementProgram } from '../__generated__/ProgramManagementProgram';
import { useCampfireTheme } from '../../../../theme/useCampfireTheme';

const UNGROUPED_DROPPABLE_ID = 'ungrouped';
const GROUP_DRAGGABLE_PREFIX = 'group-';

interface Props {
  programs: ProgramManagementProgram[];
  layout: ProgramLayout;
  onChange: (layout: ProgramLayout) => void;
}

function move<T>(list: T[], from: number, to: number) {
  const result = [...list];
  const [removed] = result.splice(from, 1);
  result.splice(to, 0, removed);
  return result;
}

export const ProgramGroupsReorderList = ({ programs, layout, onChange }: Props) => {
  const { theme } = useCampfireTheme();
  const programNames = new Map(programs.map((program) => [program.programId, program.name]));

  const getProgramIds = (droppableId: string) =>
    droppableId === UNGROUPED_DROPPABLE_ID
      ? layout.ungroupedProgramIds
      : layout.groups.find((group) => group.programGroupId === droppableId)?.programIds ?? [];

  const setProgramIds = (current: ProgramLayout, droppableId: string, programIds: string[]): ProgramLayout =>
    droppableId === UNGROUPED_DROPPABLE_ID
      ? { ...current, ungroupedProgramIds: programIds }
      : {
          ...current,
          groups: current.groups.map((group) =>
            group.programGroupId === droppableId ? { ...group, programIds } : group
          ),
        };

  const handleDragEnd = ({ type, source, destination }: DropResult) => {
    if (!destination) return;
    if (source.droppableId === destination.droppableId && source.index === destination.index) return;

    if (type === 'GROUP') {
      onChange({ ...layout, groups: move(layout.groups, source.index, destination.index) });
      return;
    }

    if (source.droppableId === destination.droppableId) {
      onChange(
        setProgramIds(
          layout,
          source.droppableId,
          move(getProgramIds(source.droppableId), source.index, destination.index)
        )
      );
      return;
    }

    const sourceIds = [...getProgramIds(source.droppableId)];
    const [programId] = sourceIds.splice(source.index, 1);
    const destinationIds = [...getProgramIds(destination.droppableId)];
    destinationIds.splice(destination.index, 0, programId);

    onChange(
      setProgramIds(setProgramIds(layout, source.droppableId, sourceIds), destination.droppableId, destinationIds)
    );
  };

  const handleAddGroup = () =>
    onChange({ ...layout, groups: [{ programGroupId: uuidv4(), name: '', programIds: [] }, ...layout.groups] });

  const handleRenameGroup = (programGroupId: string, name: string) =>
    onChange({
      ...layout,
      groups: layout.groups.map((group) => (group.programGroupId === programGroupId ? { ...group, name } : group)),
    });

  const handleRemoveGroup = (programGroupId: string) => {
    const removed = layout.groups.find((group) => group.programGroupId === programGroupId);
    onChange({
      groups: layout.groups.filter((group) => group.programGroupId !== programGroupId),
      ungroupedProgramIds: [...(removed?.programIds ?? []), ...layout.ungroupedProgramIds],
    });
  };

  const headingTextStyle = { fontSize: 13, fontWeight: 600, color: theme.color.grey.neutral500 };

  const renderPrograms = (droppableId: string, programIds: string[]) => (
    <Droppable droppableId={droppableId} type='PROGRAM'>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.droppableProps}
          style={{
            minHeight: 44,
            backgroundColor: snapshot.isDraggingOver ? theme.color.grey.background : undefined,
          }}
        >
          {programIds.map((programId, index) => (
            <Draggable key={programId} draggableId={programId} index={index}>
              {(draggableProvided) => (
                <div
                  ref={draggableProvided.innerRef}
                  {...draggableProvided.draggableProps}
                  {...draggableProvided.dragHandleProps}
                >
                  <Box
                    display='flex'
                    alignItems='center'
                    style={{
                      padding: '12px 16px 12px 24px',
                      borderBottom: `1px solid ${theme.color.grey.border}`,
                      backgroundColor: theme.color.white.neutral50,
                      cursor: 'grab',
                    }}
                  >
                    <DragIndicator style={{ fontSize: 18, color: theme.color.grey.neutral300, marginRight: 12 }} />
                    <Typography variant='subtitle2' style={{ fontWeight: 'bold' }}>
                      {programNames.get(programId)}
                    </Typography>
                  </Box>
                </div>
              )}
            </Draggable>
          ))}
          {provided.placeholder}
          {programIds.length === 0 && !snapshot.isDraggingOver && (
            <Typography variant='body2' color='textSecondary' style={{ padding: '12px 24px', fontSize: 13 }}>
              Drag programs here
            </Typography>
          )}
        </div>
      )}
    </Droppable>
  );

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Box style={{ padding: '4px 16px 8px' }}>
        <Button
          data-track='fs-prgMan-add-program-heading'
          size='small'
          startIcon={<Add />}
          onClick={handleAddGroup}
          style={{ textTransform: 'none' }}
        >
          Add heading
        </Button>
      </Box>

      <Droppable droppableId='groups' type='GROUP'>
        {(provided) => (
          <div ref={provided.innerRef} {...provided.droppableProps}>
            {layout.groups.map((group, index) => (
              <Draggable
                key={group.programGroupId}
                draggableId={`${GROUP_DRAGGABLE_PREFIX}${group.programGroupId}`}
                index={index}
              >
                {(draggableProvided) => (
                  <div ref={draggableProvided.innerRef} {...draggableProvided.draggableProps}>
                    <Box
                      display='flex'
                      alignItems='center'
                      style={{
                        padding: '12px 8px 4px 16px',
                        backgroundColor: theme.color.white.neutral50,
                        borderBottom: `1px solid ${theme.color.grey.border}`,
                      }}
                    >
                      <div {...draggableProvided.dragHandleProps} style={{ display: 'flex', cursor: 'grab' }}>
                        <DragIndicator style={{ fontSize: 18, color: theme.color.grey.neutral300, marginRight: 8 }} />
                      </div>
                      <InputBase
                        autoFocus={group.name === ''}
                        value={group.name}
                        placeholder='Heading name'
                        onChange={(event) => handleRenameGroup(group.programGroupId, event.target.value)}
                        inputProps={{ maxLength: 256, 'aria-label': 'Heading name' }}
                        style={{ ...headingTextStyle, flex: 1 }}
                      />
                      <IconButton
                        size='small'
                        aria-label='Remove heading'
                        onClick={() => handleRemoveGroup(group.programGroupId)}
                      >
                        <Close style={{ fontSize: 16 }} />
                      </IconButton>
                    </Box>
                    {renderPrograms(group.programGroupId, group.programIds)}
                  </div>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
          </div>
        )}
      </Droppable>

      {layout.groups.length > 0 && (
        <Box style={{ padding: '16px 16px 4px 42px', borderBottom: `1px solid ${theme.color.grey.border}` }}>
          <Typography style={headingTextStyle}>Ungrouped</Typography>
        </Box>
      )}
      {renderPrograms(UNGROUPED_DROPPABLE_ID, layout.ungroupedProgramIds)}
    </DragDropContext>
  );
};
